/* supportToast — error toasts that include a one-click "Open ticket" action.
 *
 * Use these wrappers any time an error comes back from a critical user action
 * (payment decline, integration push failure, label purchase rejection, etc.)
 * The toast surfaces the error AND a CTA that opens the Help & Support
 * popover with the ticket form pre-filled — subject, body, urgency, category
 * already populated with the error context. The user just confirms.
 *
 * Wiring:
 *   - errorWithTicket(message, prefill)  → sonner error toast w/ action.
 *   - openSupportTicket(prefill)         → fires the open event directly.
 *   - HelpSupportButton listens for window event "open-help-ticket" and
 *     opens the popover, switches to the Tickets tab, and pre-fills the form.
 *
 * `prefill` shape:
 *   {
 *     subject:  string,            // required for action button to show
 *     body:     string,            // pre-filled, free for the user to edit
 *     urgency:  "low"|"normal"|"high"|"urgent",  // default "high"
 *     category: string,            // optional tag (e.g. "billing", "shopify")
 *   }
 */
import { toast } from "sonner";

export const SUPPORT_EVENT = "open-help-ticket";

export function openSupportTicket(prefill = {}) {
  window.dispatchEvent(new CustomEvent(SUPPORT_EVENT, { detail: prefill }));
}

export function errorWithTicket(message, prefill = {}, options = {}) {
  const subject = prefill.subject;
  // No subject → degrade gracefully to a plain error toast (no action button).
  if (!subject) {
    toast.error(message, options);
    return;
  }
  toast.error(message, {
    duration: options.duration ?? 9000,
    description: options.description,
    action: {
      label: "Open ticket",
      onClick: () => openSupportTicket(prefill),
    },
    ...options,
  });
}

/* Convenience helper — turn an axios error into an "Open ticket" toast with
 * sane defaults. Caller provides the subject + category; we extract the
 * server's detail message and timestamp into the body. */
export function errorToastFromAxios(err, fallbackMessage, prefill = {}) {
  const detail =
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    fallbackMessage;

  const status = err?.response?.status;
  const url = err?.config?.url;
  const stamp = new Date().toISOString();

  // FastAPI routes in this app raise detail as an object: {code, message, ...}.
  // Show the server's own message rather than a generic fallback.
  const detailText =
    typeof detail === "string" ? detail : detail?.message || fallbackMessage;

  const body = [
    prefill.body,
    "",
    "──── Error context (auto-filled, please don't remove) ────",
    `Time: ${stamp}`,
    status ? `HTTP ${status}` : null,
    url ? `Endpoint: ${url}` : null,
    `Detail: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`,
  ]
    .filter((x) => x !== null && x !== undefined)
    .join("\n");

  errorWithTicket(detailText, {
    urgency: prefill.urgency || "high",
    ...prefill,
    body,
  });
}
